
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { BlogPost } from '@/types/blog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, RefreshCw, MessageSquare, ArrowRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';

interface WizardContentRefinementProps {
  initialContent: Partial<BlogPost>;
  onContentUpdated: (updatedContent: Partial<BlogPost>) => void;
}

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export const WizardContentRefinement: React.FC<WizardContentRefinementProps> = ({
  initialContent,
  onContentUpdated,
}) => {
  const [title, setTitle] = useState(initialContent.title || '');
  const [excerpt, setExcerpt] = useState(initialContent.excerpt || '');
  const [content, setContent] = useState(initialContent.content || '');
  const [tags, setTags] = useState<string[]>(initialContent.tags || []);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    { 
      role: 'assistant', 
      content: "I've created your blog draft! You can edit the content directly or chat with me to refine it. How would you like to improve it?" 
    },
  ]);
  const { toast } = useToast();

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // Add user message to chat
    const userMessage = { role: 'user' as const, content: chatInput };
    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setIsProcessing(true);

    try {
      // Send message to AI for processing
      const response = await fetch('/api/blog-ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'optimize_content',
          prompt: chatInput,
          content: content,
          title: title,
          tags: tags.join(', '),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate response');
      }

      const data = await response.json();
      
      // Update content if AI suggests changes
      if (data.content) {
        setContent(data.content);
      }
      
      // Add AI response to chat
      setChatMessages((prev) => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'I\'ve updated the content based on your feedback. You can continue to refine it or proceed to the next step.' 
        },
      ]);
      
      // If the AI suggested new titles, add them to the chat
      if (data.titleSuggestions && data.titleSuggestions.length > 0) {
        setChatMessages((prev) => [
          ...prev,
          { 
            role: 'assistant', 
            content: `Here are some title suggestions you might want to consider:\n${data.titleSuggestions.map((t: string, i: number) => `${i+1}. ${t}`).join('\n')}` 
          },
        ]);
      }
    } catch (error) {
      console.error('Error in chat processing:', error);
      toast({
        title: 'Chat Processing Error',
        description: error instanceof Error ? error.message : 'Failed to process your request',
        variant: 'destructive',
      });
      
      setChatMessages((prev) => [
        ...prev,
        { 
          role: 'assistant', 
          content: 'Sorry, I encountered an error processing your request. You can try again or continue editing manually.' 
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    onContentUpdated({
      title,
      excerpt,
      content,
      tags,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
              Excerpt
            </label>
            <Textarea
              id="excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Content (Markdown)
            </label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma separated)
            </label>
            <Input
              id="tags"
              value={tags.join(', ')}
              onChange={(e) => setTags(e.target.value.split(',').map(tag => tag.trim()))}
            />
          </div>
        </div>

        <div className="flex flex-col h-[500px] border rounded-md overflow-hidden">
          <div className="p-3 bg-gray-50 border-b">
            <h3 className="text-sm font-medium flex items-center">
              <MessageSquare className="h-4 w-4 mr-2 text-edgefi-purple" />
              Chat Assistant
            </h3>
            <p className="text-xs text-gray-500">
              Discuss changes and get suggestions for your blog post
            </p>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {chatMessages.map((message, index) => (
                <div 
                  key={index} 
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <Card className={`max-w-[80%] ${message.role === 'user' ? 'bg-edgefi-purple text-white' : 'bg-gray-100'}`}>
                    <CardContent className="p-3">
                      <p className="text-sm whitespace-pre-line">{message.content}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
              {isProcessing && (
                <div className="flex justify-start">
                  <Card className="bg-gray-100">
                    <CardContent className="p-3">
                      <div className="flex items-center">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <p className="text-sm">Processing...</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <div className="p-3 border-t">
            <form onSubmit={handleChatSubmit} className="flex gap-2">
              <Input
                placeholder="Ask for changes or suggestions..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={isProcessing}
              />
              <Button 
                type="submit" 
                disabled={isProcessing || !chatInput.trim()} 
                size="sm" 
                className="bg-edgefi-purple hover:bg-purple-700"
              >
                {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleContinue}
          className="bg-edgefi-purple hover:bg-purple-700"
        >
          Continue to Images <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
